import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, type TooltipContentProps } from "recharts";

import { AnalyticsCardTooltip } from "./AnalyticsCardTooltip";

export interface AnalyticsCardPoint {
  label: string;
  value: number;
}

export interface AnalyticsCardChartProps {
  data: AnalyticsCardPoint[];
}

// Adapts recharts' own `active`/`payload` tooltip-content contract into AnalyticsCardTooltip's
// plain `value`/`label` props -- that adaptation belongs here, next to the recharts wiring that
// requires it, not inside AnalyticsCardTooltip itself.
const renderTooltipContent = ({ active, payload, label }: TooltipContentProps<number, string>) => {
  const value = payload?.[0]?.value;
  if (!active || value === undefined || label === undefined) return null;
  return <AnalyticsCardTooltip value={value} label={String(label)} />;
};

// This design system's `color.accent` is a light neutral gray (matches `color.muted`), not a
// saturated brand hue, so it reads as nearly invisible as a line color -- the line/end-marker use
// `color.foreground` instead, the strongest-contrast neutral ink already in the token set.
/** A line graph over `data`, e.g. `<AnalyticsCard><AnalyticsCard.Chart data={series} /></AnalyticsCard>`. */
export const AnalyticsCardChart = ({ data }: AnalyticsCardChartProps) => (
  <div className="h-full w-full rounded-[calc(var(--radius)-4px)]">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <XAxis
          dataKey="label"
          axisLine={false}
          tickLine={false}
          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 8.25 }}
          interval="preserveStartEnd"
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 5.5 }}
          width={32}
        />
        <Tooltip cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }} content={renderTooltipContent} />
        <Line
          type="monotone"
          dataKey="value"
          stroke="hsl(var(--foreground))"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          isAnimationActive={false}
          dot={({ cx, cy, index }: { cx: number; cy: number; index: number }) => {
            if (index !== data.length - 1) return <span key={`dot-${index}`} />;
            return (
              <circle
                key={`dot-${index}`}
                cx={cx}
                cy={cy}
                r={4}
                fill="hsl(var(--foreground))"
                stroke="hsl(var(--card))"
                strokeWidth={2}
              />
            );
          }}
          activeDot={{ r: 4, fill: "hsl(var(--foreground))", stroke: "hsl(var(--card))", strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);
